import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { pluginApi, serverApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Loader2, Save, AlertTriangle, Users, Shield } from 'lucide-react';
import { toast } from 'sonner';

export function AntiRaidPlugin({ serverId }: { serverId: string }) {
  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const { data: channels = [] } = useQuery({
    queryKey: ['servers', serverId, 'channels'],
    queryFn: () => serverApi.getChannels(serverId),
  });

  const antiRaidPlugin = (plugins as any)?.find((p: any) => p.pluginId === 'anti-raid');
  const isEnabled = antiRaidPlugin?.enabled ?? false;

  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) => 
      enabled ? pluginApi.enablePlugin(serverId, 'anti-raid') : pluginApi.disablePlugin(serverId, 'anti-raid'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const { mutate: updateConfig, isPending: saving } = useApiMutation(
    (config: any) => pluginApi.updatePluginConfig(serverId, 'anti-raid', config),
    { 
      onSuccess: () => toast.success('Configuration saved!'),
      invalidateQueries: [['servers', serverId, 'plugins']] 
    }
  );

  const [config, setConfig] = useState<any>({
    enabled: true,
    alertChannelId: '',
    joinThreshold: 10,
    timeWindow: 60,
    action: 'alert',
    lockdownDuration: 300,
    kickNewMembers: false,
    requireVerification: false
  });

  useEffect(() => {
    if (antiRaidPlugin?.config) {
      setConfig({ ...config, ...antiRaidPlugin.config });
    }
  }, [antiRaidPlugin]);

  const handleSave = () => {
    updateConfig(config);
  };

  if (!antiRaidPlugin) return null;

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-red-500/20 rounded-xl">
            <AlertTriangle className="w-6 h-6 text-red-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Anti-Raid Protection</h3>
            <p className="text-sm text-muted-foreground">
              Protect your server from mass join raids.
            </p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={(enabled) => togglePlugin({ enabled })}
        />
      </div>

      {isEnabled && (
        <>
          {/* Detection Settings */}
          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-red-400" />
              <h4 className="font-semibold">Detection Settings</h4>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Alert Channel</Label>
                <Select
                  value={config.alertChannelId || 'none'}
                  onValueChange={(value) => setConfig({ ...config, alertChannelId: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona canale alert" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun canale</SelectItem>
                    {(channels as any[]).map((channel: any) => (
                      <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Where raid alerts will be posted.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Join Threshold</Label>
                  <Input
                    type="number"
                    min="3"
                    max="100"
                    value={config.joinThreshold}
                    onChange={(e) => setConfig({ ...config, joinThreshold: parseInt(e.target.value) })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Number of joins to trigger alert
                  </p>
                </div>

                <div>
                  <Label>Time Window (seconds)</Label>
                  <Input
                    type="number"
                    min="10"
                    max="600"
                    value={config.timeWindow}
                    onChange={(e) => setConfig({ ...config, timeWindow: parseInt(e.target.value) })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Detection time frame
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Response Actions */}
          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <h4 className="font-semibold">Response Actions</h4>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Action on Raid Detection</Label>
                <select
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                  value={config.action}
                  onChange={(e) => setConfig({ ...config, action: e.target.value })}
                >
                  <option value="alert">Alert Only</option>
                  <option value="lockdown">Lockdown Server</option>
                  <option value="kick">Kick New Members</option>
                  <option value="verification">Require Verification</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  What happens when a raid is detected
                </p>
              </div>

              {config.action === 'lockdown' && (
                <div>
                  <Label>Lockdown Duration (seconds)</Label>
                  <Input
                    type="number"
                    min="60"
                    max="3600"
                    value={config.lockdownDuration}
                    onChange={(e) => setConfig({ ...config, lockdownDuration: parseInt(e.target.value) })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    How long to prevent new joins
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Additional Options */}
          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-red-400" />
              <h4 className="font-semibold">Additional Options</h4>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <div>
                  <Label className="text-sm">Auto-Kick New Members During Raid</Label>
                  <p className="text-xs text-muted-foreground">Automatically kick accounts that join during detected raid</p>
                </div>
                <Switch
                  checked={config.kickNewMembers}
                  onCheckedChange={(checked) => setConfig({ ...config, kickNewMembers: checked })}
                />
              </div>

              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <div>
                  <Label className="text-sm">Require Verification</Label>
                  <p className="text-xs text-muted-foreground">Force new members to verify before accessing server</p>
                </div>
                <Switch
                  checked={config.requireVerification}
                  onCheckedChange={(checked) => setConfig({ ...config, requireVerification: checked })}
                />
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="glass-card p-6 bg-red-500/5 border-red-500/20">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-red-400 mb-1">How it works:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>System monitors member joins in real-time</li>
                  <li>When {config.joinThreshold}+ members join within {config.timeWindow}s, raid is detected</li>
                  <li>Configured action is automatically triggered</li>
                  <li>Staff are alerted in the designated channel</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-3">
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Configuration
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
